/**
 * HelpButton — Enhanced help icon with three options:
 * 1. AI Chat — Context-aware chat assistant
 * 2. PDF Download — One-click PDF download of the module guide
 * 3. Online Guide — Opens interactive HTML guide with hyperlinks
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { HelpCircle, X, MessageSquare, Download, BookOpen, Send, User, Bot, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

export default function HelpButton({ guideId, label }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  // Close menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const handlePdfDownload = useCallback(() => {
    setMenuOpen(false);
    // Open guide page in new window, then trigger print (which can save as PDF)
    const pw = window.open(`/guides/${guideId}`, '_blank');
    if (pw) setTimeout(() => pw.print(), 2000);
  }, [guideId]);

  const handleOnlineGuide = useCallback(() => {
    setMenuOpen(false);
    setGuideOpen(true);
  }, []);

  const handleAiChat = useCallback(() => {
    setMenuOpen(false);
    setChatOpen(true);
  }, []);

  return (
    <>
      {/* Help Button + Dropdown */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors shadow-sm"
          title={`Help: ${label || 'User Guide'}`}
          data-testid={`help-btn-${guideId}`}
        >
          <span className="text-sm font-bold leading-none">?</span>
        </button>

        {menuOpen && (
          <div className="absolute left-0 top-full mt-1 z-50 w-56 bg-popover border rounded-lg shadow-lg py-1 animate-in fade-in-0 zoom-in-95" data-testid="help-menu">
            <button
              onClick={handleAiChat}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-accent transition-colors"
              data-testid="help-ai-chat"
            >
              <MessageSquare className="h-4 w-4 text-blue-500 shrink-0" />
              <div>
                <p className="font-medium text-foreground">AI Chat Support</p>
                <p className="text-[10px] text-muted-foreground">Ask anything about Eyvo</p>
              </div>
            </button>
            <button
              onClick={handlePdfDownload}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-accent transition-colors"
              data-testid="help-pdf-download"
            >
              <Download className="h-4 w-4 text-emerald-500 shrink-0" />
              <div>
                <p className="font-medium text-foreground">Download PDF Guide</p>
                <p className="text-[10px] text-muted-foreground">Save for offline reference</p>
              </div>
            </button>
            <button
              onClick={handleOnlineGuide}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-accent transition-colors"
              data-testid="help-online-guide"
            >
              <BookOpen className="h-4 w-4 text-violet-500 shrink-0" />
              <div>
                <p className="font-medium text-foreground">Online Guide</p>
                <p className="text-[10px] text-muted-foreground">Interactive help with hyperlinks</p>
              </div>
            </button>
          </div>
        )}
      </div>

      {/* AI Chat Panel */}
      {chatOpen && <AIChatPanel onClose={() => setChatOpen(false)} guideId={guideId} label={label} />}

      {/* Online Guide Panel */}
      {guideOpen && <GuidePanel onClose={() => setGuideOpen(false)} guideId={guideId} label={label} />}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   AI CHAT PANEL
   ═══════════════════════════════════════════════════════════════ */
function AIChatPanel({ onClose, guideId, label }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', text: `Hi! I'm the Eyvo AI Assistant. Ask me anything about ${label || 'the system'} — how features work, where to find things, or best practices.` }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [humanRequested, setHumanRequested] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);

    try {
      const res = await api.post('/api/help-chat/ask', {
        message: `[User is on the ${label || 'system'} page (guide: ${guideId})]\n\n${text}`,
        session_id: sessionId,
      });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', text: res.data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: "I'm sorry, I couldn't process your request right now. Please try again or use the Online Guide for reference." }]);
    }
    setLoading(false);
  };

  const requestHuman = () => {
    setHumanRequested(true);
    setMessages(prev => [...prev, {
      role: 'assistant',
      text: "Your request for human support has been noted. A member of the Eyvo support team will follow up with you via email. In the meantime, you can continue asking me questions or check the Online Guide."
    }]);
  };

  return (
    <div className="fixed inset-0 z-[100] flex" data-testid="ai-chat-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-md bg-background shadow-2xl flex flex-col animate-in slide-in-from-right-10 duration-300">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            <div>
              <p className="text-sm font-semibold">Eyvo AI Assistant</p>
              <p className="text-[10px] opacity-80">Powered by AI — {label || 'General Help'}</p>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose} className="h-7 w-7 p-0 text-white hover:text-white hover:bg-white/20">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {m.role === 'assistant' && (
                <div className="h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <Bot className="h-3.5 w-3.5 text-blue-600" />
                </div>
              )}
              <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                m.role === 'user'
                  ? 'bg-blue-600 text-white rounded-br-sm'
                  : 'bg-muted rounded-bl-sm'
              }`}>
                <div className="whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{
                  __html: m.text
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                    .replace(/`(.*?)`/g, '<code class="bg-blue-50 text-blue-700 px-1 rounded text-xs">$1</code>')
                    .replace(/\n/g, '<br/>')
                }} />
              </div>
              {m.role === 'user' && (
                <div className="h-7 w-7 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
                  <User className="h-3.5 w-3.5 text-slate-600" />
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex gap-2 justify-start">
              <div className="h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <Bot className="h-3.5 w-3.5 text-blue-600" />
              </div>
              <div className="bg-muted rounded-xl rounded-bl-sm px-3 py-2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Human Support Button */}
        {!humanRequested && messages.length > 3 && (
          <div className="px-4 pb-2">
            <button
              onClick={requestHuman}
              className="w-full text-xs text-muted-foreground hover:text-primary py-1.5 border border-dashed rounded-lg transition-colors"
              data-testid="request-human-support"
            >
              Can't find what you need? Request human support
            </button>
          </div>
        )}

        {/* Input */}
        <div className="border-t p-3">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              placeholder="Ask about Eyvo..."
              className="flex-1 text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              disabled={loading}
              data-testid="chat-input"
            />
            <Button
              size="sm"
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              className="h-9 w-9 p-0 rounded-lg bg-blue-600 hover:bg-blue-700"
              data-testid="chat-send"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   GUIDE PANEL (Interactive Online Guide — direct render, no iframe)
   ═══════════════════════════════════════════════════════════════ */
import { GeneralSystemGuideEmbed } from './GeneralSystemGuide';
import { DataFilesGuideEmbed } from './DataFilesGuide';
import { AccountCodesGuideEmbed } from './AccountCodesGuide';
import { ContractsGuideEmbed } from './ContractsGuide';
import { RequisitionsGuideEmbed } from './RequisitionsGuide';
import { RfxSourcingGuideEmbed, PurchaseOrdersGuideEmbed, AssetRegisterGuideEmbed, InvoiceMatchingGuideEmbed, InventoryGuideEmbed, GoodsReceivingGuideEmbed, ReportsGuideEmbed } from './ModuleGuides';

const GUIDE_COMPONENTS = {
  'general': GeneralSystemGuideEmbed,
  'data-files': DataFilesGuideEmbed,
  'account-codes': AccountCodesGuideEmbed,
  'contracts': ContractsGuideEmbed,
  'requisitions': RequisitionsGuideEmbed,
  'rfx-sourcing': RfxSourcingGuideEmbed,
  'purchase-orders': PurchaseOrdersGuideEmbed,
  'asset-register': AssetRegisterGuideEmbed,
  'invoice-matching': InvoiceMatchingGuideEmbed,
  'inventory': InventoryGuideEmbed,
  'goods-receiving': GoodsReceivingGuideEmbed,
  'reports': ReportsGuideEmbed,
};

function GuidePanel({ onClose, guideId, label }) {
  const GuideComponent = GUIDE_COMPONENTS[guideId];

  return (
    <div className="fixed inset-0 z-[100] flex" data-testid={`guide-panel-${guideId}`}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-4xl bg-background shadow-2xl overflow-y-auto animate-in slide-in-from-right-10 duration-300">
        <div className="sticky top-0 z-10 bg-background border-b px-6 py-3 flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary" /> {label || 'User Guide'}
          </h2>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => window.open(`/guides/${guideId}`, '_blank')} data-testid="guide-open-fullscreen">
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Full Page
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose} className="h-7 w-7 p-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="p-6">
          {GuideComponent ? <GuideComponent /> : <p className="text-sm text-muted-foreground">Guide not found.</p>}
        </div>
      </div>
    </div>
  );
}
